import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import CustomTabs from "../../../components/Tabs/CustomTabs";
import Header from "../../../components/Header/Header";
import CustomBtn from "../../../components/Buttons/CustomBtn";
import SearchableTable from "../../../components/Tables/SearchableTable";
import { useDispatch, useSelector } from "react-redux";
import AddStockModal from "../../../components/Modals/AddStockModal";
import { fetchSupplierInvoice } from "../../../store/Slices/SupplierInvoiceSlice";
import { fetchSuppliers } from "../../../store/Slices/SupplierSlice";
import { SupplierInvoiceColumns } from "../../../assets/Columns/SupplierInvoiceColumns";
import { useNavigate } from "react-router-dom";
import { fetchCustomers } from "../../../store/Slices/CustomerSlice";
import { fetchCustomerInvoice } from "../../../store/Slices/CustomerInvoiceSlice";
import { fetchCustomerReturnInvoice } from "../../../store/Slices/CustomerReturnInvoiceSlice";
import InvoiceBrowseSection from "../../../components/Invoices/InvoiceBrowseSection";
import {
  DeleteCompanyInvoiceApi,
  DeleteCustomerInvoiceApi,
  DELETECustomerReturnInvoicesApi,
  GetCustomerInvoiceSingleApi,
  GetCustomerReturnInvoiceByIdApi,
  GetGuestCustomerInvoicesApi,
} from "../../../ApiRequests";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import ThermalCustomerInvoice from "../../../components/Reports/ThermalCustomerInvoice";
import ThermalPrintHost from "../../../components/Thermal/ThermalPrintHost";
import { mapInvoiceToThermalReceipt } from "../../../utils/thermalReceipt";
import { isGuestInvoice } from "../../../utils/invoice";
import DeleteModal from "../../../components/Modals/DeleteModal";
import EditStockModal from "../../../components/Modals/EditStockModal";
import { filterTableRowsBySearch } from "../../../utils/tableSearch";
import ExportToExcelButton from "../../../utils/ExportToExcel";
import { FiCalendar, FiPackage, FiSearch } from "react-icons/fi";
import { getAppBasePath } from "../../../utils/appPaths";
import { localYmd } from "../../../components/Reports/ReportDateRangeBar";
import { formatDateTime } from "../../../utils/dateTime";

const userData = JSON.parse(localStorage.getItem("user")) || {};
const { branchId } = userData;

const mapCompanyInvoicesForExport = (rows = []) =>
  rows.map((row) => ({
    Date: row.date ? formatDateTime(row.date) : "",
    Company: row.companyId?.name ?? row.company_name ?? "",
    "Item Name": row.itemId?.name ?? "",
    Price: Number(row.purchase ?? 0),
    Quantity: Number(row.qty ?? 0),
    "Total Amount": Number(row.total_amount ?? 0),
    Description: row.desc ?? "",
    "Truck No": row.truck_no ?? "",
    "Builti No": row.invoice_no ?? "",
  }));

const companyFieldClass =
  "w-full h-9 px-2.5 rounded-lg border border-line bg-card text-ink text-sm font-semibold outline-none focus:border-main focus:ring-2 focus:ring-main/15";

const CompanyStockSection = ({
  companies = [],
  companyFilterId,
  setCompanyFilterId,
  companyFromDate,
  setCompanyFromDate,
  companyToDate,
  setCompanyToDate,
  onApplyCompanyFilter,
  onClearCompanyDates,
  companyFilterLoading = false,
  listLoading = false,
  filteredCompanyInvoices,
  companyExportRows,
  companyExportFileName,
  SearchText,
  setSearchText,
  companyInvoicesTotalQty,
  setOpenEditModal,
  setOpenDeleteModal,
  setSelected,
}) => {
  const setTodayRange = () => {
    const today = localYmd();
    setCompanyFromDate(today);
    setCompanyToDate(today);
  };

  const clearDateRange = () => {
    setCompanyFromDate("");
    setCompanyToDate("");
    onClearCompanyDates?.();
  };

  const handleFromChange = (value) => {
    setCompanyFromDate(value);
    if (value && companyToDate && value > companyToDate) {
      setCompanyToDate(value);
    }
  };

  const handleToChange = (value) => {
    setCompanyToDate(value);
    if (value && companyFromDate && value < companyFromDate) {
      setCompanyFromDate(value);
    }
  };

  return (
    <div className="flex flex-col w-full gap-3 font-quicksand">
      <section className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
        <div className="flex items-center gap-3 px-3 py-2.5 border-b border-line bg-subtle/50">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-main text-white">
            <FiPackage size={16} />
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-ink">Company stock</h2>
            <p className="text-xs text-muted">
              All purchases shown by default — filter by date or company
            </p>
          </div>
        </div>

        <div className="px-3 py-2.5 flex flex-wrap items-end gap-2">
          <label className="block w-[132px]">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-muted mb-0.5">
              From
            </span>
            <input
              type="date"
              min="2000-01-01"
              max="2099-12-31"
              value={companyFromDate || ""}
              onChange={(e) => handleFromChange(e.target.value)}
              className={companyFieldClass}
            />
          </label>
          <label className="block w-[132px]">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-muted mb-0.5">
              To
            </span>
            <input
              type="date"
              min="2000-01-01"
              max="2099-12-31"
              value={companyToDate || ""}
              onChange={(e) => handleToChange(e.target.value)}
              className={companyFieldClass}
            />
          </label>
          <button
            type="button"
            onClick={setTodayRange}
            className="h-9 px-2.5 rounded-lg border border-line bg-subtle text-inkSecondary text-xs font-bold hover:border-main/40 hover:text-main transition-colors"
          >
            Today
          </button>
          <button
            type="button"
            onClick={clearDateRange}
            className="h-9 px-2.5 rounded-lg border border-line bg-subtle text-inkSecondary text-xs font-bold hover:border-main/40 hover:text-main transition-colors"
          >
            All dates
          </button>
          <button
            type="button"
            onClick={onApplyCompanyFilter}
            disabled={companyFilterLoading}
            className="h-9 px-3.5 rounded-lg bg-main text-white text-xs font-bold hover:bg-mainHover disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
          >
            <FiCalendar size={14} />
            {companyFilterLoading ? "…" : "Apply"}
          </button>
          <label className="block min-w-[160px] flex-1 max-w-[240px]">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-muted mb-0.5">
              Company
            </span>
            <select
              value={companyFilterId}
              onChange={(e) => setCompanyFilterId(e.target.value)}
              className={companyFieldClass}
            >
              <option value="">All companies</option>
              {(companies || []).map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-[180px] flex-[1.5]">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-muted mb-0.5">
              Search
            </span>
            <div className="relative">
              <FiSearch
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
                size={14}
              />
              <input
                type="search"
                value={SearchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Item, company, builti #, truck…"
                className={`${companyFieldClass} pl-8`}
              />
            </div>
          </label>
        </div>
      </section>

      <section className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
        <div className="px-3 py-2 border-b border-line bg-subtle/50 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-bold text-ink">
            Purchase invoices
            <span className="ml-2 text-xs font-semibold text-muted tabular-nums">
              ({filteredCompanyInvoices.length})
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-lg border border-main/20 bg-brandSoft px-3 py-1.5 text-sm">
              <span className="text-inkSecondary font-medium">Total qty</span>
              <span className="tabular-nums font-bold text-main">
                {companyInvoicesTotalQty}
              </span>
            </div>
            {filteredCompanyInvoices.length > 0 && (
              <ExportToExcelButton
                data={companyExportRows}
                fileName={companyExportFileName}
              />
            )}
          </div>
        </div>
        <div className="p-2.5">
          {listLoading ? (
            <div className="rounded-lg border border-dashed border-line bg-subtle/40 px-4 py-10 text-center text-sm text-muted animate-pulse">
              Loading company stock…
            </div>
          ) : (
            <SearchableTable
              setOpenEditModal={setOpenEditModal}
              setOpenDeleteModal={setOpenDeleteModal}
              setSelected={setSelected}
              SearchPlaceholder="Search…"
              SearchText={SearchText}
              setSearchText={setSearchText}
              CurrentData={filteredCompanyInvoices}
              Columns={SupplierInvoiceColumns}
              disableSearch
            />
          )}
        </div>
      </section>
    </div>
  );
};

const Invoices = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [SearchText, setSearchText] = useState("");
  const [saleType, setSaleType] = useState("SALE");
  const [partyType, setPartyType] = useState("CUSTOMER");
  const [guestInvoices, setGuestInvoices] = useState([]);
  const [guestSearch, setGuestSearch] = useState("");
  const [guestLoading, setGuestLoading] = useState(false);
  const [filterCustomerId, setFilterCustomerId] = useState("");
  const [invoiceListSearch, setInvoiceListSearch] = useState("");
  const [fromDate, setFromDate] = useState(() => localYmd());
  const [toDate, setToDate] = useState(() => localYmd());
  const [appliedFromDate, setAppliedFromDate] = useState(() => localYmd());
  const [appliedToDate, setAppliedToDate] = useState(() => localYmd());
  const [filterLoading, setFilterLoading] = useState(false);
  const [OpenCompanyModal, setOpenCompanyModal] = useState(false);
  const [SelectedCustomer, setSelectedCustomer] = useState("");
  const [companyFilterId, setCompanyFilterId] = useState("");
  const [companyFromDate, setCompanyFromDate] = useState(() => localYmd());
  const [companyToDate, setCompanyToDate] = useState(() => localYmd());
  const [appliedCompanyFromDate, setAppliedCompanyFromDate] = useState(() =>
    localYmd()
  );
  const [appliedCompanyToDate, setAppliedCompanyToDate] = useState(() =>
    localYmd()
  );
  const [companyFilterLoading, setCompanyFilterLoading] = useState(false);
  const [SelectedInvoice, setSelectedInvoice] = useState("");
  const [OpenEditModal, setOpenEditModal] = useState(false);
  const [OpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [Selected, setSelected] = useState("");
  const [Loading, setLoading] = useState(false);
  const [deleteReturnLoading, setDeleteReturnLoading] = useState(false);
  const [deleteInvoiceLoading, setDeleteInvoiceLoading] = useState(false);
  const [thermalReceiptData, setThermalReceiptData] = useState(null);
  const thermalPrintRef = useRef(null);
  const printPendingRef = useRef(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const CompanyInvoices = useSelector((state) => state.SupplierInvoices);
  const Suppliers = useSelector((state) => state.Suppliers);
  const CustomerInvoices = useSelector((state) => state.CustomerInvoices);
  const CustomerReturnInvoices = useSelector(
    (state) => state.CustomerReturnInvoices
  );

  const isGuestParty = partyType === "GUEST";

  useEffect(() => {
    if (activeTab !== 1) return;
    dispatch(fetchSuppliers());
    dispatch(fetchSupplierInvoice());
  }, [activeTab, dispatch]);

  useEffect(() => {
    setSelectedInvoice("");
    setSelectedCustomer("");
    setFilterCustomerId("");
    setInvoiceListSearch("");
  }, [partyType]);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  const applyCompanyStockFilters = async () => {
    setCompanyFilterLoading(true);
    try {
      await dispatch(fetchSupplierInvoice());
      setAppliedCompanyFromDate(companyFromDate || "");
      setAppliedCompanyToDate(companyToDate || "");
    } finally {
      setCompanyFilterLoading(false);
    }
  };

  const buildRangeParams = (start, end) => {
    const params = {
      startDate: start,
      endDate: end || start,
    };
    if (branchId) params.branchId = branchId;
    return params;
  };

  const loadGuestSaleInvoices = async (start, end, search = guestSearch) => {
    const response = await GetGuestCustomerInvoicesApi({
      ...buildRangeParams(start, end),
      search: search.trim() || undefined,
    });
    setGuestInvoices(response?.data?.data?.payload || []);
  };

  const applyInvoiceFilters = async (
    start = fromDate,
    end = toDate,
    options = {}
  ) => {
    const { clearSelection = true } = options;
    if (!start) return;
    const rangeEnd = end || start;
    setFilterLoading(true);
    if (clearSelection) setSelectedInvoice("");
    try {
      await Promise.all([
        dispatch(fetchCustomerInvoice(buildRangeParams(start, rangeEnd))),
        dispatch(fetchCustomerReturnInvoice(buildRangeParams(start, rangeEnd))),
      ]);
      if (partyType === "GUEST" && saleType === "SALE") {
        await loadGuestSaleInvoices(start, rangeEnd);
      }
      setAppliedFromDate(start);
      setAppliedToDate(rangeEnd);
    } catch {
      ErrorToast("Failed to load invoices for selected dates");
    }
    setFilterLoading(false);
  };

  const handleApplyFilter = () => {
    applyInvoiceFilters(fromDate, toDate);
  };

  // Initial load: today
  useEffect(() => {
    if (activeTab !== 0) return;
    applyInvoiceFilters(localYmd(), localYmd(), { clearSelection: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount / tab only
  }, [activeTab]);

  const displayGuestInvoices = useMemo(() => {
    if (saleType === "SALE") return guestInvoices;
    return (CustomerReturnInvoices.data || []).filter(isGuestInvoice);
  }, [saleType, guestInvoices, CustomerReturnInvoices.data]);

  useEffect(() => {
    if (!isGuestParty || saleType !== "SALE") return;

    let cancelled = false;
    const loadGuestInvoices = async () => {
      setGuestLoading(true);
      try {
        await loadGuestSaleInvoices(appliedFromDate, appliedToDate, guestSearch);
      } catch {
        if (!cancelled) {
          ErrorToast("Failed to load guest invoices");
          setGuestInvoices([]);
        }
      }
      if (!cancelled) setGuestLoading(false);
    };

    const timer = setTimeout(loadGuestInvoices, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuestParty, saleType, guestSearch, appliedFromDate, appliedToDate, branchId]);

  const activeInvoice = useMemo(() => {
    if (!SelectedInvoice) return null;
    if (isGuestParty) {
      return displayGuestInvoices.find((dt) => dt._id === SelectedInvoice) || null;
    }
    if (saleType === "SALE") {
      return CustomerInvoices.data?.find((dt) => dt._id === SelectedInvoice);
    }
    return CustomerReturnInvoices.data?.find(
      (dt) => dt._id === SelectedInvoice
    );
  }, [
    SelectedInvoice,
    isGuestParty,
    saleType,
    displayGuestInvoices,
    CustomerInvoices.data,
    CustomerReturnInvoices.data,
  ]);

  const handleSelectInvoice = (invoice) => {
    if (!invoice?._id) return;
    setSelectedInvoice(invoice._id);
    if (invoice.customerId && typeof invoice.customerId === "object") {
      setSelectedCustomer(invoice.customerId);
    } else if (!isGuestInvoice(invoice)) {
      setSelectedCustomer("");
    } else {
      setSelectedCustomer("");
    }
  };

  const handleEditInvoice = (invoice) => {
    if (!invoice?._id) return;
    const base = getAppBasePath();
    if (saleType === "RETURN") {
      navigate(`${base}/edit-customers-return/${invoice._id}`);
    } else {
      navigate(`${base}/edit-customers-invoice/${invoice._id}`);
    }
  };

  const handleThermalPrint = useReactToPrint({
    content: () => thermalPrintRef.current,
    documentTitle: `Invoice-${thermalReceiptData?.invoice_no || "receipt"}`,
    removeAfterPrint: true,
    onAfterPrint: () => {
      printPendingRef.current = false;
      setThermalReceiptData(null);
    },
  });

  useEffect(() => {
    if (!printPendingRef.current || !thermalReceiptData) return;
    printPendingRef.current = false;
    const timer = setTimeout(() => handleThermalPrint(), 350);
    return () => clearTimeout(timer);
  }, [thermalReceiptData, handleThermalPrint]);

  const handlePrintInvoice = async (invoice) => {
    if (!invoice?._id) return;
    try {
      let full = invoice;
      if (saleType === "RETURN") {
        const res = await GetCustomerReturnInvoiceByIdApi(invoice._id);
        full = res?.data?.data?.payload ?? invoice;
      } else if (
        isGuestInvoice(invoice) ||
        !invoice.items?.length ||
        !invoice.items[0]?.name
      ) {
        const res = await GetCustomerInvoiceSingleApi(invoice._id);
        full = res?.data?.data?.payload ?? invoice;
      }
      printPendingRef.current = true;
      setThermalReceiptData(
        mapInvoiceToThermalReceipt(full, {
          isReturn: saleType === "RETURN",
          printMode: isGuestInvoice(full) ? "hideItemPrices" : "full",
        })
      );
    } catch {
      ErrorToast("Could not load invoice for thermal print");
    }
  };

  const companyInvoicesList = useMemo(() => {
    let rows = Array.isArray(CompanyInvoices.data) ? CompanyInvoices.data : [];

    if (companyFilterId) {
      rows = rows.filter((row) => {
        const cid =
          row.companyId?._id?.toString() || row.companyId?.toString() || "";
        return cid === companyFilterId;
      });
    }

    const start = appliedCompanyFromDate
      ? moment(appliedCompanyFromDate, "YYYY-MM-DD").startOf("day").unix()
      : null;
    const end = appliedCompanyToDate
      ? moment(appliedCompanyToDate, "YYYY-MM-DD").endOf("day").unix()
      : null;

    if (start != null || end != null) {
      rows = rows.filter((row) => {
        const d = Number(row.date) || 0;
        if (start != null && d < start) return false;
        if (end != null && d > end) return false;
        return true;
      });
    }

    return [...rows].sort((a, b) => (b.date || 0) - (a.date || 0));
  }, [
    CompanyInvoices.data,
    companyFilterId,
    appliedCompanyFromDate,
    appliedCompanyToDate,
  ]);

  const filteredCompanyInvoices = useMemo(
    () =>
      filterTableRowsBySearch(
        companyInvoicesList,
        SearchText,
        SupplierInvoiceColumns
      ),
    [companyInvoicesList, SearchText]
  );

  const companyInvoicesTotalQty = useMemo(
    () =>
      filteredCompanyInvoices.reduce(
        (sum, dt) => sum + Number(dt.qty ?? 0),
        0
      ),
    [filteredCompanyInvoices]
  );

  const companyExportRows = useMemo(
    () => mapCompanyInvoicesForExport(filteredCompanyInvoices),
    [filteredCompanyInvoices]
  );

  const companyExportFileName = useMemo(() => {
    const company = (Suppliers.data || []).find(
      (c) => c._id === companyFilterId
    );
    const name = String(company?.name || "All Companies").trim();
    const safe = name.replace(/[^\w\s-]/g, "").replace(/\s+/g, " ");
    return `Company Stock - ${safe || "All"}`;
  }, [Suppliers.data, companyFilterId]);

  const handlePrint = () => {
    if (!activeInvoice) return;
    handlePrintInvoice(activeInvoice);
  };

  const refreshGuestSaleInvoices = async () => {
    try {
      await loadGuestSaleInvoices(appliedFromDate, appliedToDate);
    } catch {
      setGuestInvoices([]);
    }
  };

  const handleDeleteReturn = async () => {
    if (!SelectedInvoice) return;
    if (!isGuestParty && !SelectedCustomer) return;
    if (
      !window.confirm(
        "Delete this return? Stock and customer balance will be restored."
      )
    ) {
      return;
    }
    setDeleteReturnLoading(true);
    try {
      await DELETECustomerReturnInvoicesApi(SelectedInvoice);
      SuccessToast("Return invoice deleted");
      setSelectedInvoice("");
      dispatch(
        fetchCustomerReturnInvoice(
          buildRangeParams(appliedFromDate, appliedToDate)
        )
      );
      dispatch(fetchCustomers());
    } catch {
      ErrorToast("Failed to delete return invoice");
    }
    setDeleteReturnLoading(false);
  };

  const handleDeleteInvoice = async (invoice) => {
    const target = invoice || activeInvoice;
    if (!target?._id || saleType === "RETURN") return;
    if (
      !window.confirm(
        "Delete this sale invoice? Item stock and customer account will be restored."
      )
    ) {
      return;
    }
    setDeleteInvoiceLoading(true);
    try {
      await DeleteCustomerInvoiceApi(target._id);
      SuccessToast("Sale invoice deleted");
      if (SelectedInvoice === target._id) {
        setSelectedInvoice("");
        setSelectedCustomer("");
      }
      dispatch(
        fetchCustomerInvoice(buildRangeParams(appliedFromDate, appliedToDate))
      );
      dispatch(fetchCustomers());
      if (isGuestParty) {
        await refreshGuestSaleInvoices();
      }
    } catch (err) {
      ErrorToast(
        err.response?.data?.error?.msg || "Failed to delete sale invoice"
      );
    }
    setDeleteInvoiceLoading(false);
  };

  return (
    <div className="w-full flex flex-col px-4 pb-10 font-quicksand">
      <Header
        title=""
        desc=""
      >
        <CustomBtn title="Add stock" onClick={() => setOpenCompanyModal(true)} />
        <CustomBtn
          title="Add multiple stock"
          onClick={() => navigate(`${getAppBasePath()}/add-stock-multiple`)}
        />
      </Header>

      <div className="mb-4">
        <CustomTabs
          tabs={[{ label: "Customer / Guest" }, { label: "Company stock" }]}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>

      {activeTab === 0 && (
        <InvoiceBrowseSection
          saleType={saleType}
          setSaleType={setSaleType}
          partyType={partyType}
          setPartyType={setPartyType}
          isGuestParty={isGuestParty}
          guestSearch={guestSearch}
          setGuestSearch={setGuestSearch}
          guestLoading={
            isGuestParty && saleType === "RETURN"
              ? CustomerReturnInvoices.loading
              : guestLoading
          }
          guestInvoices={displayGuestInvoices}
          filterCustomerId={filterCustomerId}
          setFilterCustomerId={setFilterCustomerId}
          invoiceListSearch={invoiceListSearch}
          setInvoiceListSearch={setInvoiceListSearch}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          appliedFromDate={appliedFromDate}
          appliedToDate={appliedToDate}
          onApplyFilter={handleApplyFilter}
          filterLoading={filterLoading}
          setSelectedCustomer={setSelectedCustomer}
          CustomerInvoices={CustomerInvoices}
          CustomerReturnInvoices={CustomerReturnInvoices}
          SelectedInvoice={SelectedInvoice}
          setSelectedInvoice={setSelectedInvoice}
          activeInvoice={activeInvoice}
          onPrint={handlePrint}
          onDeleteReturn={handleDeleteReturn}
          deleteReturnLoading={deleteReturnLoading}
          onDeleteInvoice={handleDeleteInvoice}
          deleteInvoiceLoading={deleteInvoiceLoading}
          onSelectInvoice={handleSelectInvoice}
          onEditInvoice={handleEditInvoice}
          onPrintInvoice={handlePrintInvoice}
        />
      )}

      <ThermalPrintHost
        data={thermalReceiptData}
        printRef={thermalPrintRef}
        component={ThermalCustomerInvoice}
      />

      {activeTab === 1 && (
        <>
          <CompanyStockSection
            companies={Suppliers.data || []}
            companyFilterId={companyFilterId}
            setCompanyFilterId={setCompanyFilterId}
            companyFromDate={companyFromDate}
            setCompanyFromDate={setCompanyFromDate}
            companyToDate={companyToDate}
            setCompanyToDate={setCompanyToDate}
            onApplyCompanyFilter={applyCompanyStockFilters}
            onClearCompanyDates={() => {
              setAppliedCompanyFromDate("");
              setAppliedCompanyToDate("");
            }}
            companyFilterLoading={companyFilterLoading}
            listLoading={CompanyInvoices.loading}
            filteredCompanyInvoices={filteredCompanyInvoices}
            companyExportRows={companyExportRows}
            companyExportFileName={companyExportFileName}
            SearchText={SearchText}
            setSearchText={setSearchText}
            companyInvoicesTotalQty={companyInvoicesTotalQty}
            setOpenEditModal={setOpenEditModal}
            setOpenDeleteModal={setOpenDeleteModal}
            setSelected={setSelected}
          />
          {OpenDeleteModal && (
            <DeleteModal
              Open={OpenDeleteModal}
              setOpen={setOpenDeleteModal}
              Loading={Loading}
              onSubmit={async () => {
                setLoading(true);
                try {
                  const response = await DeleteCompanyInvoiceApi(Selected._id);
                  if (response.data.success) {
                    setOpenDeleteModal(false);
                    SuccessToast("Company invoice deleted");
                    dispatch(fetchSupplierInvoice());
                  } else {
                    ErrorToast(response.data.error?.msg);
                  }
                } catch (err) {
                  ErrorToast(
                    err.response?.data?.error?.msg ||
                      "Failed to delete invoice"
                  );
                }
                setLoading(false);
              }}
            />
          )}
          {OpenEditModal && Selected && (
            <EditStockModal
              stockDetail={Selected}
              Open={OpenEditModal}
              setOpen={setOpenEditModal}
              supplierId={
                Selected?.companyId?._id || Selected?.companyId || undefined
              }
            />
          )}
        </>
      )}

      {OpenCompanyModal && (
        <AddStockModal Open={OpenCompanyModal} setOpen={setOpenCompanyModal} />
      )}
    </div>
  );
};

export default Invoices;
